import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle } from "lucide-react";

// Poses cut from the official character sheet; each has one job (see design plan).
const ALT = {
  full: "Bryn, the Bryn AI mascot, cheering",
  happy: "Bryn smiling",
  thinking: "Bryn thinking",
  surprised: "Bryn looking surprised",
  determined: "Bryn looking determined",
  excited: "Bryn celebrating",
  wink: "Bryn winking",
};

export function Mascot({ pose = "happy", className = "h-16 w-16", eager = false, decorative = false }) {
  return (
    <img src={`/mascot/${pose}.webp`} alt={decorative ? "" : ALT[pose]} aria-hidden={decorative || undefined}
      loading={eager ? "eager" : "lazy"} decoding="async" draggable="false"
      className={`object-contain select-none pointer-events-none ${className}`} data-testid={`mascot-${pose}`} />
  );
}

// Empty screens: a related pose, one short sentence and one clear next step.
export function EmptyState({ pose = "thinking", title, text, action, to, onAction, testid = "empty-state" }) {
  const btn = "inline-flex items-center justify-center h-11 px-5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500";
  return (
    <div className="soft-card px-6 py-10 flex flex-col items-center text-center" data-testid={testid}>
      <Mascot pose={pose} className="h-20 w-20 mb-4" decorative />
      {title && <p className="font-display text-lg font-semibold">{title}</p>}
      {text && <p className="text-sm text-neutral-400 mt-1 max-w-sm">{text}</p>}
      {action && (to
        ? <Link to={to} className={`${btn} mt-5`} data-testid={`${testid}-action`}>{action}</Link>
        : <button onClick={onAction} className={`${btn} mt-5`} data-testid={`${testid}-action`}>{action}</button>)}
    </div>
  );
}

export function ErrorState({ text = "Something didn't load.", onRetry, testid = "error-state" }) {
  return (
    <div className="soft-card px-6 py-8 flex flex-col items-center text-center" data-testid={testid}>
      <Mascot pose="surprised" className="h-16 w-16 mb-3" decorative />
      <p className="text-sm text-neutral-300">{text}</p>
      {onRetry && <button onClick={onRetry} className="mt-4 h-10 px-4 rounded-full border border-white/15 text-sm hover:bg-white/5" data-testid={`${testid}-retry`}>Try again</button>}
    </div>
  );
}

export function SkeletonList({ rows = 3, testid = "skeleton-list" }) {
  return (
    <div className="space-y-3" data-testid={testid} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton h-16" />)}
    </div>
  );
}

// A short, still congratulation after real achievements only.
export function Celebrate({ title, text, testid = "celebrate" }) {
  return (
    <div className="pop soft-card px-5 py-4 flex items-center gap-4 border-[rgba(255,213,79,0.35)]" role="status" data-testid={testid}>
      <span className="relative shrink-0 h-14 w-14">
        <Mascot pose="excited" className="h-14 w-14 mascot-celebrate" decorative />
        {SPARKS.map((s, i) => <Sparkle key={i} aria-hidden="true" className="sparkle absolute h-3 w-3 text-brand-yellow fill-current" style={s} />)}
      </span>
      <div>
        <p className="font-display font-semibold text-brand-yellow">{title}</p>
        {text && <p className="text-sm text-neutral-300 mt-0.5">{text}</p>}
      </div>
    </div>
  );
}

const SPARKS = [
  { top: "-6px", left: "-4px", animationDelay: "0ms" },
  { top: "2px", right: "-8px", animationDelay: "260ms" },
  { bottom: "-2px", left: "-8px", animationDelay: "520ms" },
];

const prefersStill = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Live "Thinking…" before the first word: Bryn bobs and ponders, the dots pulse.
export function ThinkingMascot() {
  return (
    <span className="flex items-center gap-2 text-neutral-400 text-sm" data-testid="thinking-indicator" role="status" aria-label="Thinking">
      <Mascot pose="thinking" className="h-9 w-9 mascot-think" eager decorative />
      <span>Thinking</span>
      <span className="think-dots" aria-hidden="true"><i /><i /><i /></span>
    </span>
  );
}

// Safari can't show transparent WebM, so it gets the transparent animated picture instead.
const canAlphaWebm = () => {
  const ua = navigator.userAgent;
  const safari = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|CriOS\/|Edg\/|FxiOS\/|Firefox\//.test(ua);
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return !safari && !ios && !!document.createElement("video").canPlayType?.('video/webm; codecs="vp9"');
};

// Welcome: the waving clip with its background removed, so Bryn sits directly on the page.
export function WelcomeMascot({ className = "" }) {
  const [mode] = useState(() => (prefersStill() ? "still" : canAlphaWebm() ? "video" : "anim"));
  const [failed, setFailed] = useState(false);
  const cls = `block shrink-0 aspect-square object-contain select-none pointer-events-none ${className}`;
  if (mode === "video" && !failed) {
    return <MascotVideo src="/mascot/welcome-alpha.webm?v=3" poster="/mascot/welcome-still.webp?v=3" label="Bryn waving hello" eager className={className}
      onFail={() => setFailed(true)} testid="welcome-mascot-video" />;
  }
  return <img src={mode === "still" ? "/mascot/welcome-still.webp?v=3" : "/mascot/welcome-alpha.webp?v=3"} alt="Bryn waving hello" className={cls}
    draggable="false" data-testid={mode === "still" ? "welcome-mascot-still" : "welcome-mascot-anim"} />;
}

// Looping mascot clip: muted, inline, no controls. Plays only while on screen and the tab is visible;
// shows the still frame for reduced motion or if playback can't start.
export function MascotVideo({ src, poster, label, className = "", eager = false, testid = "mascot-video", onFail }) {
  const ref = useRef(null);
  const [still, setStill] = useState(prefersStill);
  useEffect(() => {
    const v = ref.current;
    if (!v || still) return undefined;
    let onScreen = true;
    const sync = () => {
      if (onScreen && !document.hidden) v.play().catch(() => {});
      else v.pause();
    };
    const io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; sync(); });
    io.observe(v);
    document.addEventListener("visibilitychange", sync);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", sync); v.pause(); };
  }, [still]);
  const cls = `block shrink-0 aspect-square object-contain select-none pointer-events-none ${className}`;
  if (still) return <img src={poster} alt={label} className={cls} draggable="false" data-testid={`${testid}-still`} />;
  return (
    <video ref={ref} src={src} poster={poster} autoPlay muted loop playsInline disablePictureInPicture disableRemotePlayback
      preload={eager ? "auto" : "metadata"} aria-label={label} onError={() => (onFail ? onFail() : setStill(true))} className={cls} data-testid={testid} />
  );
}
